/*global swal*/
import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import Bowser from 'bowser';
import Video from 'twilio-video';
import cn from 'classnames';
import { interval } from 'd3-timer';

import ErrorModal from './components/ErrorModal';
import SignalLevel from './components/SignalLevel';

const browser = Bowser.getParser(window.navigator.userAgent);
const isDesktop = () => 'desktop' === browser.getPlatformType();

const AudioContext = window.AudioContext || window.webkitAudioContext;
let audioContext;
const initializeAnalyser = function (stream) {
	audioContext = audioContext || new AudioContext();
	const audioSource = audioContext.createMediaStreamSource(stream);

	const analyser = audioContext.createAnalyser();
	analyser.smoothingTimeConstant = 0.4;
	analyser.fftSize = 512;

	audioSource.connect(analyser);
	return analyser;
};

class VideoChat extends Component {
	constructor(props) {
		super(props);

		this.localRef = createRef();
		this.remoteRef = createRef();

		this.state = {
			patientAccepted: false,
			isCalling: true,
			disconnected: false,
			isConnecting: true,
			isConnected: false,
			isMuted: true,
			isRemoteMuted: false,
			remoteLevel: 0,
			localLevel: 5,
			showRemoteAudio: false,
			remoteVolume: '21',
			isErrorModalOpen: false,
		};
	}

	disconnect = () => {
		if (this.timer) {
			this.timer.stop();
		}

		this.setState({ showRemoteAudio: false });

		if (this.room) {
			this.room.localParticipant.tracks.forEach((publication) => {
				publication.track.detach();
				publication.track.stop();
				this.room.localParticipant.unpublishTrack(publication.track);
			});

			if (this.room && this.room.state === 'connected') {
				this.room.participants.forEach((participant) => {
					participant.tracks.forEach((publication) => {
						if (publication.track.kind !== 'data') {
							publication.track.detach();
						}
					});
				});
				this.room.disconnect();
			}
		}

		if (window.opener) {
			window.close();
		} else {
			this.props.onClose();
		}
	};

	videoAccepted = () => {
		this.setState({ patientAccepted: true });
		swal.close();
	};

	videoDeclined = () => {
		swal(
			{
				title: 'Video Chat Declined',
				text: 'Patient has declined to connect via Video Chat.',
				type: 'warning',
			},
			this.disconnect
		);
	};

	videoEnded = () => {
		this.setState({ disconnected: true }, () => {
			this.disconnect();
		});
	};

	joinSuccess = (room) => {
		// console.log(`Connected to "${room}" Room`);
		this.room = room;
		this.room.localParticipant.tracks.forEach((publication) => {
			const track = publication.track;

			if (this.localRef.current) {
				this.localRef.current.appendChild(track.attach());
			}
		});
		this.room.localParticipant.on('networkQualityLevelChanged', (level) => {
			this.setState({ localLevel: level });
		}); // local mute button

		this.setState({ isMuted: false });
		this.room.participants.forEach(this.onParticipantConnected);
		this.room.on('participantConnected', this.onParticipantConnected);
		this.room.on('participantDisconnected', this.handleParticipantDisconnected);
		this.room.once('disconnected', this.handleDisconnection);

		this.missingPatient = setTimeout(() => {
			this.setState({ isCalling: false });

			if (!this.state.patientAccepted) {
				swal(
					{
						title: 'Patient Missed Call',
						text: 'Patient was not available, ending video chat.',
						type: 'warning',
					},
					this.endCall
				);
			}
		}, 25000);
	};

	handleParticipantDisconnected = (participant) => {
		setTimeout(() => {
			if (!this.state.disconnected && participant.identity === 'patient') {
				swal(
					{
						title: 'Patient Disconnected',
						text: "There was a problem with the patient's connection and was disconnected. Recommend sending a new Video Chat request to continue.",
						type: 'warning',
					},
					this.disconnect
				);
			}
		}, 250);
	};

	joinFailure = (error) => {
		this.setState({
			joinError: error,
			isErrorModalOpen: true,
		});
	};

	endCall = () => {
		this.props.appSocket.emit('video end');
		this.setState({ disconnected: true }, () => {
			this.disconnect();
		});
	};

	handleDisconnection = () => {
		if (!this.state.disconnected) {
			swal(
				{
					title: 'Disconnected',
					text: 'There was a problem with your connection and you were disconnected. Recommend checking your network connection and sending a new Video Chat request to continue.',
					type: 'warning',
				},
				this.disconnect
			);
		}
	};

	attachRemoteTrack = (track) => {
		if (track.kind === 'video') {
			this.setState({
				isConnecting: false,
				isConnected: true,
			});
			track.on('started', () => {
				this.setState({ isCalling: false });
			});
		}

		if (track.kind === 'audio') {
			track.on('enabled', () => {
				this.setState({ isRemoteMuted: false });
			});
			track.on('disabled', () => {
				this.setState({ isRemoteMuted: true });
			});
			track.on('stopped', () => {
				if (this.timer) {
					this.timer.stop();
				}

				this.setState({ showRemoteAudio: false });
			});
			track.on('started', () => {
				if (track.mediaStreamTrack) {
					this.setState({ showRemoteAudio: true });
					const analyser = initializeAnalyser(
						new MediaStream([track.mediaStreamTrack])
					);
					const sampleArray = new Uint8Array(analyser.frequencyBinCount);
					this.timer = interval(() => {
						analyser.getByteFrequencyData(sampleArray);
						let values = 0;
						const length = sampleArray.length;

						for (let i = 0; i < length; i++) {
							values += sampleArray[i];
						}

						const volume = Math.min(
							21,
							Math.max(0, Math.log10(values / length / 3) * 14)
						);
						this.setState({ remoteVolume: String(21 - volume) });
					}, 50);
				}
			});
		}

		if (track.kind !== 'data') {
			if (this.remoteRef.current) {
				this.remoteRef.current.appendChild(track.attach());
			}
		}
	};

	onParticipantConnected = (participant) => {
		// console.log(`Participant "${participant.identity}" is connected to the Room`);
		if (
			participant &&
			participant.identity &&
			participant.identity === 'patient'
		) {
			this.videoAccepted();
			participant.on('networkQualityLevelChanged', (level) => {
				this.setState({ remoteLevel: level });
			});
		}

		participant.tracks.forEach((publication) => {
			if (publication.isSubscribed) {
				this.attachRemoteTrack(publication.track);
			}
		});
		participant.on('trackSubscribed', this.attachRemoteTrack);
		participant.on('trackUnsubscribed', (track) => {
			if (track.kind === 'audio') {
				if (this.timer) {
					this.timer.stop();
				}

				this.setState({ showRemoteAudio: false });
			}

			if (track.kind !== 'data') {
				const mediaElements = track.detach();
				mediaElements.forEach((mediaElement) => mediaElement.remove());
			}
		});
	};

	handleMuteToggle = () => {
		this.setState((currState) => {
			if (this.room) {
				this.room.localParticipant.audioTracks.forEach((publication) => {
					if (currState.isMuted) {
						publication.track.enable();
					} else {
						publication.track.disable();
					}
				});
			}
			return { isMuted: !currState.isMuted };
		});
	};

	componentDidMount() {
		const { appSocket, token, room: roomName } = this.props;
		appSocket.on('video start', this.videoAccepted);
		appSocket.on('video decline', this.videoDeclined);
		appSocket.on('video end', this.videoEnded);
		const bandwidthProfile = {
			video: {
				mode: 'grid',
			},
		};

		if (!isDesktop()) {
			bandwidthProfile.video.maxSubscriptionBitrate = 2500000;
		}

		Video.connect(token, {
			name: roomName,
			networkQuality: {
				local: 1,
				remote: 2,
			},
			maxAudioBitrate: 16000,
			bandwidthProfile,
		}).then(this.joinSuccess, this.joinFailure);
	}

	componentWillUnmount() {
		this.props.appSocket.off('video end');
		this.props.appSocket.off('video decline');
		this.props.appSocket.off('video start');
		clearTimeout(this.missingPatient);
		if (this.timer) {
			this.timer.stop();
		}
	}

	render() {
		const {
			isMuted,
			remoteLevel,
			isRemoteMuted,
			showRemoteAudio,
			remoteVolume,
			localLevel,
			isCalling,
			isConnecting,
			isConnected,
			isErrorModalOpen,
			joinError,
		} = this.state;

		return (
			<div className="video-wrapper">
				<div className="video-controls">
					<button
						className="btn btn-oval btn-lg btn-danger end-call mr"
						onClick={this.endCall}
					>
						<i className="icon-call-end icons" />
					</button>
					<button
						className={cn('btn', 'btn-oval', 'btn-lg', {
							'btn-default': isMuted,
							'btn-danger': !isMuted,
						})}
						onClick={this.handleMuteToggle}
					>
						<i
							className={cn('fa', {
								'fa-microphone': !isMuted,
								'fa-microphone-slash': isMuted,
							})}
						/>
					</button>
				</div>
				<div className="videos">
					<div className="caller" id="peer" ref={this.remoteRef} />
					<div className="caller-signal">
						<div className="pull-left">
							<SignalLevel level={remoteLevel} />
						</div>
						<div className="pull-left mute-indicator">
							<span
								className={cn('fa-stack', 'remote-is-muted', {
									hidden: !isRemoteMuted,
								})}
							>
								<em className="fa fa-circle fa-stack-2x text-danger"></em>
								<em className="fa fa-microphone-slash fa-stack-1x fa-inverse text-white"></em>
							</span>
						</div>
						<div
							className={cn('remote-audio', 'pull-left', {
								hidden: !isRemoteMuted || !showRemoteAudio,
							})}
						>
							<svg
								focusable="false"
								viewBox="0 0 24 24"
								aria-hidden="true"
								height="24px"
								width="24px"
							>
								<defs>
									<clipPath id="remote-audio-activity">
										<rect x="0" y={remoteVolume} width="24" height="24" />
									</clipPath>
								</defs>
								<path
									fill="rgba(255, 255, 255, 0.8)"
									d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"
								></path>
								<path
									fill="#0c0"
									clipPath="url(#remote-audio-activity)"
									d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"
								></path>
							</svg>
						</div>
					</div>
					<div className="self" id="self" ref={this.localRef} />
					<div className="self-signal">
						<SignalLevel level={localLevel} />
					</div>
					{isCalling && (
						<div className="calling">
							<div>
								<span className="fa fa-spin fa-spinner" />
								&nbsp;
								{isConnecting && (
									<span className="connecting">
										Waiting for Patient to pick up
									</span>
								)}
								{isConnected && (
									<span className="connected">
										Connected. Waiting for Patient video stream.
									</span>
								)}
							</div>
						</div>
					)}
				</div>
				<ErrorModal
					isOpen={isErrorModalOpen}
					error={joinError}
					onClose={this.endCall}
				/>
			</div>
		);
	}
}

VideoChat.propTypes = {
	room: PropTypes.string.isRequired,
	token: PropTypes.string.isRequired,
	appSocket: PropTypes.object.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default VideoChat;
